import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import Card from '../components/Card';
import Button from '../components/Button';
import { useSession } from '../context/SessionContext';

const SessionInfo = () => {
  const { sessionId } = useParams();
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const audienceLinkRef = useRef(null);
  const displayLinkRef = useRef(null);

  // Fetch session info
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch session info
        const response = await axios.get(`/api/sessions/${sessionId}`);
        setSessionInfo(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching session info:', err);
        setError('Failed to load session information.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [sessionId]);

  // Generate links
  const baseUrl = window.location.origin;
  const audienceLink = `${baseUrl}/session/${sessionId}/audience`;
  const displayLink = `${baseUrl}/session/${sessionId}/display`;

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopied(type);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleCopyAudienceLink = () => {
    copyToClipboard(audienceLink, 'audience');
  };

  const handleCopyDisplayLink = () => {
    copyToClipboard(displayLink, 'display');
  };

  if (loading) {
    return (
      <Layout title="Loading Session Info...">
        <div className="text-center py-8">
          <p>Loading session information...</p>
        </div>
      </Layout>
    );
  }

  if (error || !sessionInfo) {
    return (
      <Layout title="Session Not Found">
        <Card>
          <div className="text-center py-4">
            <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
            <p className="mb-4">{error || 'The session information could not be loaded.'}</p>
            <Link to="/">
              <Button>Return to Home</Button>
            </Link>
          </div>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout 
      title="Session Information" 
      backLink={`/session/${sessionId}/manage`}
    >
      <div className="max-w-3xl mx-auto">
        <Card title={sessionInfo.name}>
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Session Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Session ID</p>
                <p className="font-medium">{sessionInfo.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created On</p>
                <p className="font-medium">{new Date(sessionInfo.createdAt).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-medium">
                  <span className="inline-block px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    Active
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Questions</p>
                <p className="font-medium">{sessionInfo.questionCount || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Audience Link</h3>
            <p className="text-sm text-gray-500 mb-2">
              Share this link with your audience to collect questions:
            </p>
            <div className="flex items-center">
              <input
                ref={audienceLinkRef}
                type="text"
                value={audienceLink}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50"
              />
              <Button
                onClick={handleCopyAudienceLink}
                className="rounded-l-none"
              >
                {copied === 'audience' ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <div className="mt-2 flex gap-2">
              <a 
                href={`https://twitter.com/intent/tweet?text=Submit%20your%20questions%20here:%20${encodeURIComponent(audienceLink)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600"
              >
                Share on Twitter
              </a>
              <a 
                href={`mailto:?subject=Submit your questions&body=Please submit your questions using this link: ${audienceLink}`}
                className="inline-flex items-center px-3 py-1 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600"
              >
                Share via Email
              </a>
            </div>
          </div>
          
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Display Link</h3>
            <p className="text-sm text-gray-500 mb-2">
              Open this link on your presentation screen to display questions:
            </p>
            <div className="flex items-center">
              <input
                ref={displayLinkRef}
                type="text"
                value={displayLink}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50"
              />
              <Button
                onClick={handleCopyDisplayLink}
                className="rounded-l-none"
              >
                {copied === 'display' ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>
          
          <div className="flex gap-4 justify-between">
            <Link to={`/session/${sessionId}/manage`}>
              <Button variant="outline">
                Back to Question Manager
              </Button>
            </Link>
            <Link to={`/session/${sessionId}/templates`}>
              <Button>
                Manage Display Templates
              </Button>
            </Link>
          </div>
        </Card>
        
        <div className="mt-6">
          <Card title="QR Codes">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="text-center">
                <h3 className="font-medium mb-2">Audience Link QR Code</h3>
                <div className="bg-white p-4 inline-block rounded-md border border-gray-200">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(audienceLink)}`} 
                    alt="Audience QR Code" 
                    className="mx-auto"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Scan to submit questions
                </p>
              </div>
              
              <div className="text-center">
                <h3 className="font-medium mb-2">Display Link QR Code</h3>
                <div className="bg-white p-4 inline-block rounded-md border border-gray-200">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(displayLink)}`} 
                    alt="Display QR Code" 
                    className="mx-auto"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Scan to open display view
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default SessionInfo; 